import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '../Container';
import { getStorage } from '../../helpers/Storage';
import AuthService from '../../services/AuthService';
import Image from '../Image';

import { gtmAttributesMakeLogin, gtmAttributesMakeLogout } from '../../utils/gtmVariables';
import useGTMEventsTracker from '../../hooks/useGTMEventsTracker';
import { sairButtonStyles } from './index.styles';

interface HeaderProps {
  logo: string;
  hamburgerIcon: string;
  closeIcon: string;
  hasRecord: string;
  login: string;
  maintenance?: boolean;
  frequentQuestions?: string;
}

const Header = ({
  logo,
  hamburgerIcon,
  closeIcon,
  hasRecord,
  login,
  maintenance,
  frequentQuestions,
}: HeaderProps) => {
  const [clicked, setClicked] = useState(false);
  const [hasLogin, setHasLogin] = useState(false);
  const [userName, setUserName] = useState(() => {
    const name = getStorage('sellerName');
    if (name) return name;
    return 'Nome do usuário';
  });

  const gtmEventsTracker = useGTMEventsTracker();
  const navigate = useNavigate();

  const handleClick = () => setClicked((prev) => !prev);

  const makeLogin = () => {
    gtmEventsTracker(gtmAttributesMakeLogin);
    navigate('/login');
  };

  const makeLogout = () => {
    gtmEventsTracker(gtmAttributesMakeLogout);
    try {
      AuthService.logout();
    } finally {
      navigate('/login');
    }
  };

  useEffect(() => {
    if (/true/i.test(getStorage('isLogged'))) {
      setHasLogin(true);
      setUserName(getStorage('sellerName'));
    } else {
      setHasLogin(false);
    }
  });

  return (
    <nav id="mp__header" className="w-full bg-primary relative">
      {/* Desktop */}
      <Container className="hidden sm:flex items-center h-20">
        <Image
          images={[
            { link: '/', img: `${logo}`, alt: 'marketplace-logo', id: 'mp__header-link-logo' },
          ]}
        />
        {!maintenance && (
          <div className="ml-auto flex items-center gap-12">
            {frequentQuestions && (
              <span className="text-sm text-neutral-lightest">
                <span className="font-normal">Dúvidas? </span>
                <span className="font-bold">{frequentQuestions}</span>
              </span>
            )}
            {hasLogin ? (
              <>
                <span className="text-sm font-normal text-neutral-lightest">{userName}</span>
                <button className={sairButtonStyles({ variant: 'outline' })} onClick={makeLogout}>
                  Sair
                </button>
              </>
            ) : (
              <button
                id="mp__header-link-login"
                type="button"
                className="text-sm text-neutral-lightest bg-transparent border-0 p-0 cursor-pointer"
                onClick={makeLogin}
              >
                <span id="mp__header-text-ja-tem-cadastro" className="font-normal">
                  {hasRecord}{' '}
                </span>
                <span className="font-bold">{login}</span>
              </button>
            )}
          </div>
        )}
      </Container>

      {/* Mobile — barra superior */}
      <div className="relative flex items-center h-20 sm:hidden">
        {!maintenance && (
          <button
            id="mp__header-btn-toggle-menu"
            type="button"
            className="py-8 px-6 bg-transparent border-0 focus:outline-none"
            onClick={handleClick}
            aria-controls="responsive-navbar-nav"
            aria-expanded={clicked}
            aria-label={clicked ? 'Fechar menu' : 'Abrir menu'}
          >
            <img className="w-5 h-4" src={clicked ? closeIcon : hamburgerIcon} alt="menu-icon" />
          </button>
        )}
        <div className="flex-1 flex justify-start px-6">
          <Image
            images={[
              {
                link: '/',
                img: `${logo}`,
                alt: 'marketplace-logo',
                id: 'mp__header-link-logo-mobile',
              },
            ]}
          />
        </div>
      </div>

      {/* Mobile — dropdown */}
      {!maintenance && clicked && (
        <div
          id="responsive-navbar-nav"
          className="bg-primary rounded-b-lg shadow-[0_4px_4px_rgba(33,33,33,0.12)] flex flex-col items-center justify-center gap-6 py-8 lg:hidden"
        >
          {hasLogin ? (
            <>
              <span className="text-sm font-normal text-neutral-lightest">{userName}</span>
              {frequentQuestions && (
                <span className="text-sm text-neutral-lightest no-underline cursor-pointer">
                  <span className="font-normal">Dúvidas? </span>
                  <span className="font-bold">{frequentQuestions}</span>
                </span>
              )}
              <button className={sairButtonStyles({ variant: 'filled' })} onClick={makeLogout}>
                Sair
              </button>
            </>
          ) : (
            <>
              <button
                id="mp__header-link-login-mobile"
                type="button"
                className="text-sm text-neutral-lightest bg-transparent border-0 p-0 cursor-pointer"
                onClick={makeLogin}
              >
                <span id="mp__header-text-ja-tem-cadastro-mobile" className="font-normal">
                  {hasRecord}{' '}
                </span>
                <span className="font-bold">{login}</span>
              </button>
              {frequentQuestions && (
                <span className="text-sm text-neutral-lightest no-underline cursor-pointer">
                  <span className="font-normal">Dúvidas? </span>
                  <span className="font-bold">{frequentQuestions}</span>
                </span>
              )}
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Header;
